/**
 * One row of the outline, recursive.
 *
 * The row's controls come from `block.actions`, which the server computes. That is not
 * defensiveness for its own sake: a block linked to a content library, a released timed
 * exam and a split-test child each forbid a different subset of delete/drag/duplicate,
 * for reasons the browser cannot see. Deriving the buttons from category instead would
 * offer actions the server then refuses, and the author would have no idea why.
 *
 * Nesting is fixed at three levels by the platform (section → subsection → unit), but the
 * names and the child type are read from `child_info` rather than hardcoded — they are
 * translated strings and an install can change them.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, Icon } from '../../shared/ui';
import type { OutlineBlock } from '../../shared/lib/studio';
import { isUnscheduled } from './dates';

/** Drag in progress. Held by the page so any row can see it, not just the dragged one. */
export interface DragState {
  locator: string;
  parentLocator: string;
  index: number;
}

export interface OutlineOps {
  expanded: Set<string>;
  toggle: (locator: string) => void;
  rename: (locator: string, name: string) => void;
  addChild: (parentLocator: string, category: string, label: string) => void;
  publish: (locator: string) => void;
  discard: (block: OutlineBlock) => void;
  duplicate: (parentLocator: string, locator: string) => void;
  remove: (block: OutlineBlock, label: string) => void;
  configure: (block: OutlineBlock, label: string) => void;
  /** Reorder within one parent. `to` is the destination index in the current list. */
  move: (parentLocator: string, from: number, to: number) => void;
  /** Locator of whatever write is in flight, so only that row shows as busy. */
  pending: string | null;
  drag: DragState | null;
  setDrag: (drag: DragState | null) => void;
  /** Route to a unit's own page. */
  unitHref: (locator: string) => string;
}

/** Visibility state as something an author can act on, rather than an enum name. */
function statusOf(block: OutlineBlock): { label: string; tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' } {
  switch (block.visibility_state) {
    case 'live':
      return { label: 'Live', tone: 'success' };
    case 'ready':
      return { label: 'Scheduled', tone: 'info' };
    case 'staff_only':
      return { label: 'Hidden', tone: 'warning' };
    case 'gated':
      return { label: 'Prerequisite', tone: 'info' };
    case 'hide_from_toc':
      return { label: 'Not in outline', tone: 'warning' };
    case 'needs_attention':
      // The one state that always needs a person: descendants disagree with each other.
      return { label: 'Needs attention', tone: 'danger' };
    default:
      return { label: 'Unscheduled', tone: 'neutral' };
  }
}

export function OutlineNode({
  block,
  parentLocator,
  index,
  siblingCount,
  label,
  ops,
}: {
  block: OutlineBlock;
  parentLocator: string;
  index: number;
  siblingCount: number;
  /** This block's own type name, from its parent's `child_info.display_name`. */
  label: string;
  ops: OutlineOps;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.display_name);
  const [dropSide, setDropSide] = useState<'before' | 'after' | null>(null);

  const childInfo = block.child_info;
  const children = childInfo?.children ?? [];
  const isUnit = block.category === 'vertical';
  const open = ops.expanded.has(block.id);
  const status = statusOf(block);
  const busy = ops.pending === block.id;

  function commitRename() {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== block.display_name) ops.rename(block.id, next);
    else setDraft(block.display_name);
  }

  // A rename that lost the race with a refetch would otherwise leave the input showing a
  // stale name for as long as the row is mounted.
  useEffect(() => setDraft(block.display_name), [block.display_name]);

  const draggable = block.actions.draggable && siblingCount > 1;

  return (
    <li
      className="outline-node"
      data-depth={block.category}
      data-drop={dropSide ?? undefined}
      data-busy={busy || undefined}
      onDragOver={(event) => {
        // Only same-parent reordering is a drop target. Moving between parents is a
        // different server call with different validation, offered from the row menu
        // instead, so accepting it here would quietly do the wrong thing.
        const drag = ops.drag;
        if (!drag || drag.parentLocator !== parentLocator || drag.locator === block.id) return;
        event.preventDefault();
        event.stopPropagation();
        setDropSide(drag.index < index ? 'after' : 'before');
      }}
      onDragLeave={() => setDropSide(null)}
      onDrop={(event) => {
        const drag = ops.drag;
        setDropSide(null);
        if (!drag || drag.parentLocator !== parentLocator || drag.locator === block.id) return;
        event.preventDefault();
        event.stopPropagation();
        ops.move(parentLocator, drag.index, index);
        ops.setDrag(null);
      }}
    >
      <div className="outline-row">
        {draggable ? (
          <span
            className="outline-row__grip"
            draggable
            onDragStart={() => ops.setDrag({ locator: block.id, parentLocator, index })}
            onDragEnd={() => ops.setDrag(null)}
            aria-hidden="true"
            title="Drag to reorder"
          >
            <Icon name="drag_indicator" />
          </span>
        ) : (
          <span className="outline-row__grip is-empty" aria-hidden="true" />
        )}

        {block.has_children || !isUnit ? (
          <button
            type="button"
            className="outline-row__twist"
            onClick={() => ops.toggle(block.id)}
            aria-expanded={open}
            aria-label={open ? `Collapse ${block.display_name}` : `Expand ${block.display_name}`}
          >
            <Icon name={open ? 'expand_more' : 'chevron_right'} />
          </button>
        ) : (
          <span className="outline-row__twist is-empty" aria-hidden="true" />
        )}

        <span className="outline-row__kind">{label}</span>

        <div className="outline-row__title">
          {editing ? (
            <RenameInput
              value={draft}
              onChange={setDraft}
              onCommit={commitRename}
              onCancel={() => {
                setDraft(block.display_name);
                setEditing(false);
              }}
            />
          ) : (
            <>
              {isUnit ? (
                <a href={ops.unitHref(block.id)} className="outline-row__link">
                  {block.display_name}
                </a>
              ) : (
                <span className="outline-row__name">{block.display_name}</span>
              )}
              <button
                type="button"
                className="outline-row__rename"
                onClick={() => setEditing(true)}
                aria-label={`Rename ${block.display_name}`}
              >
                <Icon name="edit" />
              </button>
            </>
          )}

          <div className="outline-row__meta">
            <Badge tone={status.tone}>{status.label}</Badge>
            {block.has_changes && block.published && <Badge tone="warning">Draft changes</Badge>}
            {!block.published && <Badge tone="neutral">Never published</Badge>}
            {!isUnscheduled(block.start) && block.release_date && (
              <span className="outline-row__date">
                <Icon name="schedule" />
                {block.release_date}
              </span>
            )}
            {block.graded && block.format && (
              <span className="outline-row__date">
                <Icon name="assignment" />
                {block.format}
                {block.due_date ? ` · due ${block.due_date}` : ''}
              </span>
            )}
            {block.upstream_info?.upstream_ref && (
              <Badge tone={block.upstream_info.ready_to_sync ? 'warning' : 'info'}>
                {block.upstream_info.ready_to_sync ? 'Library update available' : 'From library'}
              </Badge>
            )}
          </div>
        </div>

        <div className="outline-row__actions">
          {siblingCount > 1 && block.actions.draggable && (
            // The keyboard path to the same thing drag does. Not optional — drag and drop
            // is unusable without a pointer, and reordering is a core action here.
            <>
              <IconButton
                icon="arrow_upward"
                label={`Move ${block.display_name} up`}
                disabled={index === 0}
                onClick={() => ops.move(parentLocator, index, index - 1)}
              />
              <IconButton
                icon="arrow_downward"
                label={`Move ${block.display_name} down`}
                disabled={index === siblingCount - 1}
                onClick={() => ops.move(parentLocator, index, index + 1)}
              />
            </>
          )}
          {block.has_changes && (
            <IconButton
              icon="publish"
              label={`Publish ${block.display_name}`}
              onClick={() => ops.publish(block.id)}
            />
          )}
          {block.has_changes && block.published && (
            <IconButton
              icon="undo"
              label={`Discard changes to ${block.display_name}`}
              onClick={() => ops.discard(block)}
            />
          )}
          <IconButton
            icon="tune"
            label={`${label} settings`}
            onClick={() => ops.configure(block, label)}
          />
          {block.actions.duplicable && (
            <IconButton
              icon="content_copy"
              label={`Duplicate ${block.display_name}`}
              onClick={() => ops.duplicate(parentLocator, block.id)}
            />
          )}
          {block.actions.deletable && (
            <IconButton
              icon="delete"
              label={`Delete ${block.display_name}`}
              danger
              onClick={() => ops.remove(block, label)}
            />
          )}
        </div>
      </div>

      {open && (
        <div className="outline-node__children">
          {children.length > 0 && (
            <ul className="outline-list">
              {children.map((child, childIndex) => (
                <OutlineNode
                  key={child.id}
                  block={child}
                  parentLocator={block.id}
                  index={childIndex}
                  siblingCount={children.length}
                  label={childInfo?.display_name ?? child.category}
                  ops={ops}
                />
              ))}
            </ul>
          )}

          {/* Units hold components, which are edited on the unit's own page — offering
              "add component" here would mean rebuilding the block editors in a tree row. */}
          {childInfo && block.actions.childAddable && !isUnit && (
            <button
              type="button"
              className="outline-add"
              onClick={() => ops.addChild(block.id, childInfo.category, childInfo.display_name)}
            >
              <Icon name="add" />
              New {childInfo.display_name.toLowerCase()}
            </button>
          )}

          {isUnit && (
            <a className="outline-add" href={ops.unitHref(block.id)}>
              <Icon name="edit_note" />
              Edit content
            </a>
          )}
        </div>
      )}
    </li>
  );
}

/** Inline rename. Escape reverts, Enter and blur both commit — the usual contract. */
function RenameInput({
  value,
  onChange,
  onCommit,
  onCancel,
}: {
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.select(), []);

  return (
    <input
      ref={ref}
      className="outline-row__input"
      value={value}
      aria-label="Name"
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
        if (e.key === 'Escape') onCancel();
      }}
    />
  );
}

function IconButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className="outline-icon-btn"
      data-danger={danger || undefined}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Icon name={icon} />
    </button>
  );
}
